import { useEffect, useRef, useState, type Ref } from 'react';
import {
  AVATAR_UPLOAD_CONTENT_TYPES,
  AVATAR_UPLOAD_MAX_BYTES,
  avatarUrlSchema,
  BIO_MAX_LENGTH,
  type OwnProfile,
} from '@molt/shared';
import { Avatar, Button, CardSurface, InlineEdit, TextField } from '@/components';
import { formatAttachmentSize } from '@/lib/attachments';
import { useInterfaceLanguage } from '@/lib/interface-language';

/**
 * The member's own profile, viewed and edited (`FE-06.2`, `FR-PERM-002`).
 * `D1-BRIEF.md` §6.6 fixes the four regions, their order, and every
 * accessible name (§7); this file is presentational — props in, callbacks
 * out — and owns none of the data layer.
 *
 * **"Presentational" here means no data or router hooks** — no `useQuery`,
 * `useMutation`, `useSession`, `useNavigate`, and no `announce()` (the
 * **route** announces a save, §8.2). `useState`/`useRef`/`useEffect` are
 * still used, because the brief's own region 2 fixes a *live* counter
 * (`counter={{ value, ... }}`) and `TextField` is controlled — neither is
 * driveable from `profile` alone. That is the one hook-shaped decision this
 * file makes for itself; the brief's "no hooks" is read as "no data hooks".
 * `useInterfaceLanguage` (FE-80.1) joins them on the same reading — it reads a
 * context, issues no query and no mutation, and is argued at its call site.
 *
 * ── The draft/prop resync, and why it is not a plain `useState` ──────────
 * `profile.bio` and `profile.avatarUrl` change out from under this component
 * whenever the route's mutation resolves (`D1-BRIEF.md` §5.5 — every patch
 * response is a complete `ownProfileSchema` written into the `['me']`
 * cache). A draft that never resynced would show a save that failed as if it
 * had worked once a *different* field's save re-rendered this panel; a draft
 * that always resynced would erase a save that failed and discard exactly
 * the text `InlineEdit` deliberately preserves. `InlineEdit` itself resolves
 * this with `useEffect(() => { if (status === 'idle') setDraft(value) },
 * [status, value])` — this file has no `status`, so it tracks the same idea
 * with a ref: resync only when the draft still equals the *previously known*
 * committed value, i.e. nothing was typed since the last time the prop and
 * the draft agreed. A failed save leaves `profile.bio` unchanged, so the
 * effect never fires for it and the typed text survives; a successful save
 * already set the draft to the trimmed value it sent, so the two agree by
 * construction and the resync is a no-op.
 *
 * ── The avatar preview never shares the `img` role with the identity avatar ──
 * `Avatar size="md"` in region 1 sits directly beside the display-name
 * editor, which already names the member — a second announced name for the
 * same avatar is exactly the double-naming `Avatar`'s own docstring argues
 * against for `alt=""`. It renders `decorative`. Region 4's preview is the
 * only avatar in this file with a spoken name (`profileName(profile)`,
 * fixed by §7), and it is `Avatar` that supplies that name — this file does
 * not restate it.
 *
 * ── The initials are never recomputed ─────────────────────────────────────
 * `profile.initials` and `profile.initialsColour` are server-derived
 * (`apps/server/src/auth/profile.ts`) and passed straight to `Avatar`, which
 * refuses to re-derive them for the same reason (`D1-BRIEF.md` §3.3 item 3).
 *
 * ── Read-only identity fields ──────────────────────────────────────────────
 * `@username` and the email render as static text with a one-line hint. The
 * email has no route that changes it — `/auth/change-email` is on Better
 * Auth's `disabledPaths`. The username DOES have routes now —
 * `PATCH /me/username` and `GET /users/username-availability` (`BE-44.6`,
 * `FR-NOTIF-033` AC3/AC4) — and this panel still renders it as static text,
 * because a rename control is a form of its own (availability lookup,
 * conflict copy, reclaim rules) and no requirement in this block designs it.
 * Static text with a hint is the surface `BE-44.6` leaves, not a control
 * that could not work.
 *
 * ⚠ **The paragraph above replaces one that expired when the routes
 * shipped.** The `FE-44.2` contract chunk refused the rename for three
 * measured reasons, any one of which was sufficient at the time:
 * `user.username_changed` absent from both live CHECKs (migration since
 * written: `0059_username_history`), no table for a former username (since
 * built: `user_username_history`), and no caller for an availability check
 * (since built beside the rename). `FE-44.2`'s residual is closed on the
 * server; what remains is the rename FORM, which belongs to a frontend
 * chunk, not to this one.
 *
 * ── The avatar: ONE section, TWO channels ─────────────────────────────────
 * ⚠ **This paragraph replaces one that expired the moment a picker shipped.**
 * `FR-NOTIF-033` AC1 asks for an upload, and there is one:
 *
 *   `POST /me/avatar/presign`          → a signed PUT **and** the durable URL
 *   `GET  /users/:id/avatar/:avatarId` → 302 to a freshly signed GET
 *
 * with **no confirm route** — the client PUTs the bytes straight to storage
 * and then sends the returned URL through the same `PATCH /me/avatar` this
 * panel's `onAvatarChange` already calls (`apps/server/src/routes/profile-avatar.ts`
 * has the argument). So the URL field below is not a field with nothing
 * behind it: the URL it accepts is exactly the URL a presign hands back.
 *
 * The retired text said the file picker was missing from this file "for a
 * reason", and named `routes/profile.tsx` as the composition site that would
 * own its three network calls. Half of that survived and half did not. The
 * picker is now **in this file**, inside the Avatar section — and the three
 * network calls are **still the route's**, reached through the `avatarUpload`
 * prop below. Presentational never meant "renders no picker"; it meant "owns
 * no fetch", and it still does.
 *
 * ⚠ **`h3`, not `h2`, and the level is load-bearing.** `D1-BRIEF.md` §6.6
 * fixes four regions and their order; the picker is a sub-region of Avatar,
 * not a fifth peer of it. This file's own test reads
 * `getAllByRole('heading', { level: 2 })` and must still see exactly those
 * four, while `FR-NOTIF-033.spec.ts` asks for a heading named "Upload an
 * avatar" with no level at all. One `h3` satisfies both, and neither
 * assertion had to be touched to make the move land.
 *
 * ⚠ **The picker renders LAST inside the section**, after Save and Remove.
 * `profile.avatarUpload.hint` says uploading "replaces the avatar link
 * above", and above is where that link now is. Promoting the picker would
 * make a shipped sentence false for the sake of an ordering nobody asked for.
 *
 * ⚠ **The two shared constants are not a data hook.**
 * `AVATAR_UPLOAD_CONTENT_TYPES` feeds `accept` and `AVATAR_UPLOAD_MAX_BYTES`
 * feeds `{size}`; both are what the SERVER signs, so the dialog cannot offer
 * a type the route will 422 and the sentence cannot claim a cap that moved.
 * `formatAttachmentSize` is a pure function that takes `t`. None of the three
 * issues a query or a mutation, so the props-in/callbacks-out contract at the
 * top of this docstring is intact.
 *
 * **What remains open is the rename FORM** — the routes above are
 * `BE-44.6`'s; a control that calls them belongs to a frontend chunk. The
 * avatar half of `FE-44.2` is done.
 */
export interface ProfilePanelProps {
  profile: OwnProfile;
  onDisplayNameChange: (displayName: string) => void | Promise<void>;
  /** Empty means `null`, never `''` — `bioSchema` rejects the empty string. */
  onBioChange: (bio: string | null) => void | Promise<void>;
  onAvatarChange: (avatarUrl: string | null) => void | Promise<void>;
  /**
   * The avatar FILE picker, wired by the composition site — `FE-44.2`.
   *
   * ⚠ **One optional bundle, and NOT the loose `onAvatarFile` the residual
   * that asked for this move promised.** Two optional props that must co-occur
   * have a legal-looking failure mode: a file input rendered with no submit
   * button beside it, which is the same "control that could not work" the
   * username paragraph above refuses. `exactOptionalPropertyTypes` would then
   * want a conditional spread at the call site, and a conditional JSX spread
   * is a hole in `tsc` rather than a style choice.
   *
   * So every field in here is REQUIRED and the bundle itself is optional — a
   * consumer that passes nothing renders this panel exactly as it rendered
   * before a picker existed, which is what keeps every sibling render site
   * compiling unchanged. Measured after the move: 39 of the 49 `<Panel …>`
   * renders in `ProfilePanel.test.tsx` pass no bundle, and the one real
   * consumer (`routes/profile.tsx`) passes one.
   *
   * ⚠ **This bundle carries no network call.** The presign, the signed upload
   * and the avatar patch all stay in the route; so does the chosen `File`, so
   * does the choose-time refusal, and so does the ref the route clears the
   * input through once an upload has succeeded.
   */
  avatarUpload?: {
    /** The chosen file, or `null` when the member cleared the input. */
    onFile: (file: File | null) => void;
    onSubmit: () => void;
    /**
     * The route's half of the two-flight race: the panel's typed-URL save and
     * this upload write the SAME column, so each makes the other unavailable.
     * The mirror is `busy.avatar`, which the route ORs with its upload state.
     */
    submitDisabled: boolean;
    busy: boolean;
    /** A local refusal or a failed upload. `null`, never the empty string. */
    message: string | null;
    inputRef: Ref<HTMLInputElement>;
  };
  busy?: { displayName?: boolean; bio?: boolean; avatar?: boolean };
  error?: { displayName?: string; bio?: string; avatar?: string };
}

/**
 * `TextField`'s own rule: a counter earns its place only once it has
 * something to say. `D1-BRIEF.md` §6.6 fixes both numbers directly
 * (`{ max: 500, warnAt: 450 }`); `BIO_MAX_LENGTH` is the wire limit and this
 * is the same 50-character margin `CardComposer` and `ListComposer` use for
 * the same field-length reason.
 */
const BIO_COUNTER_WARN_AT = BIO_MAX_LENGTH - 50;

export function ProfilePanel({
  profile,
  onDisplayNameChange,
  onBioChange,
  onAvatarChange,
  avatarUpload,
  busy,
  error,
}: ProfilePanelProps) {
  /*
   * FE-80.1 — the one hook this file's "presentational" rule admits alongside
   * the draft state. `useInterfaceLanguage` is not a DATA hook: it reads a
   * context the shell already populated, issues no query and no mutation, and
   * so leaves the props-in/callbacks-out contract in §6.6 intact. Every other
   * routed file in this slice reaches `t` the same way.
   */
  const { t } = useInterfaceLanguage();
  const [bioDraft, setBioDraft] = useState(profile.bio ?? '');
  const previousBioRef = useRef(profile.bio);

  useEffect(() => {
    setBioDraft((current) =>
      current === (previousBioRef.current ?? '') ? (profile.bio ?? '') : current,
    );
    previousBioRef.current = profile.bio;
  }, [profile.bio]);

  const [avatarDraft, setAvatarDraft] = useState(profile.avatarUrl ?? '');
  const previousAvatarUrlRef = useRef(profile.avatarUrl);
  /*
   * FE-80.1 — this holds a FLAG, not the message it used to hold. Storing the
   * translated sentence in state would freeze the copy of whatever catalogue
   * was live at the moment the URL failed to parse, and the catalogue is a
   * query: it arrives after the first paint and refetches when the member
   * changes their interface language, so a stored string would keep answering
   * in the previous language until the field was edited again. Translating at
   * render costs nothing and cannot go stale.
   */
  const [avatarUrlInvalid, setAvatarUrlInvalid] = useState(false);

  useEffect(() => {
    setAvatarDraft((current) =>
      current === (previousAvatarUrlRef.current ?? '')
        ? (profile.avatarUrl ?? '')
        : current,
    );
    previousAvatarUrlRef.current = profile.avatarUrl;
  }, [profile.avatarUrl]);

  function handleBioSave() {
    const trimmed = bioDraft.trim();
    setBioDraft(trimmed);
    void onBioChange(trimmed === '' ? null : trimmed);
  }

  function handleAvatarSave() {
    const trimmed = avatarDraft.trim();
    if (trimmed === '') return; // The one channel for clearing is Remove.
    const result = avatarUrlSchema.safeParse(trimmed);
    if (!result.success) {
      setAvatarUrlInvalid(true);
      return;
    }
    setAvatarUrlInvalid(false);
    setAvatarDraft(result.data);
    void onAvatarChange(result.data);
  }

  function handleAvatarRemove() {
    setAvatarUrlInvalid(false);
    setAvatarDraft('');
    void onAvatarChange(null);
  }

  /*
   * A `const` alias, and the aliasing is the point rather than brevity.
   * `avatarUpload` is a destructured PARAMETER, which TypeScript treats as
   * mutable, so narrowing it with `!== undefined` does not survive into the
   * `onChange` and `onClick` closures below — every access inside them would
   * be "possibly undefined". A `const` binding keeps its narrowing inside
   * nested functions, so this one line is what lets the picker read
   * `upload.onFile` in a callback without a non-null assertion.
   */
  const upload = avatarUpload;

  const avatarSaveDisabled = avatarDraft.trim() === '';
  const avatarFieldError = avatarUrlInvalid
    ? t('profile.panel.avatarUrlInvalid')
    : error?.avatar;

  // `exactOptionalPropertyTypes` means a prop typed `foo?: string` refuses an
  // explicit `undefined` — only an OMITTED key satisfies it. `busy`/`error`
  // here are themselves optional (`ProfilePanelProps`), so their per-field
  // reads are `T | undefined`, and each spread below turns "undefined" into
  // "absent" rather than passing the value through unchanged.
  const displayNameInlineEditProps = {
    ...(busy?.displayName !== undefined ? { busy: busy.displayName } : {}),
    ...(error?.displayName !== undefined ? { error: error.displayName } : {}),
  };
  const bioFieldProps = {
    ...(error?.bio !== undefined ? { error: error.bio } : {}),
  };
  const avatarFieldProps = {
    ...(avatarFieldError !== undefined ? { error: avatarFieldError } : {}),
  };

  return (
    <div className="flex flex-col gap-6">
      <section aria-labelledby="profile-identity-heading" className="flex flex-col gap-3">
        <h2 id="profile-identity-heading" className="text-base font-semibold text-ink">
          {t('profile.panel.identity')}
        </h2>

        <CardSurface variant="sheet" className="flex flex-col gap-4 p-4">
          <div className="flex items-center gap-3">
            <Avatar profile={profile} size="md" decorative />
            <div className="min-w-0 flex-1">
              {/*
                One key, two call sites: the caption and the `InlineEdit`
                label, which is the control's accessible NAME. They must stay
                byte-identical — the caption is what a sighted member reads and
                the label is what a screen reader announces for the same field,
                and two keys could drift into naming one control two ways.
              */}
              <span className="mb-1 block text-sm font-medium text-ink">
                {t('profile.panel.displayName')}
              </span>
              <InlineEdit
                value={profile.displayName}
                label={t('profile.panel.displayName')}
                onCommit={onDisplayNameChange}
                {...displayNameInlineEditProps}
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="min-w-0">
              <span className="block text-sm font-medium text-ink">
                {t('profile.panel.username')}
              </span>
              <p className="break-all text-ink">@{profile.username}</p>
              <p className="mt-1 text-xs text-ink-muted">
                {t('profile.panel.usernameHint')}
              </p>
            </div>
            <div className="min-w-0">
              <span className="block text-sm font-medium text-ink">
                {t('profile.panel.email')}
              </span>
              <p className="break-all text-ink">{profile.email}</p>
              <p className="mt-1 text-xs text-ink-muted">
                {t('profile.panel.emailHint')}
              </p>
            </div>
          </div>
        </CardSurface>
      </section>

      <section aria-labelledby="profile-bio-heading" className="flex flex-col gap-3">
        <h2 id="profile-bio-heading" className="text-base font-semibold text-ink">
          {t('profile.panel.bio')}
        </h2>

        <CardSurface variant="sheet" className="flex flex-col gap-3 p-4">
          {/* The second of the two-site keys: heading and field label, for the
              same reason `displayName` above is one key. */}
          <TextField
            label={t('profile.panel.bio')}
            multiline
            value={bioDraft}
            onChange={(event) => {
              setBioDraft(event.target.value);
            }}
            maxLength={BIO_MAX_LENGTH}
            counter={{
              value: bioDraft.length,
              max: BIO_MAX_LENGTH,
              warnAt: BIO_COUNTER_WARN_AT,
            }}
            {...bioFieldProps}
          />
          <div>
            {/*
              Save bio accepts an EMPTY field — trimmed-empty is bio's own
              clearing channel (`onBioChange(null)`, §3.3 item 2). Save avatar
              below refuses the same shape and routes clearing through Remove
              instead, because `Remove avatar` (§6.6 item 3) is that field's
              one channel. The asymmetry is deliberate, not an oversight: bio
              has no Remove control to route through, so Save has to carry
              both jobs; avatar does, so Save carries only one.
            */}
            <Button
              variant="primary"
              size="sm"
              busy={busy?.bio ?? false}
              onClick={handleBioSave}
            >
              {t('profile.panel.saveBio')}
            </Button>
          </div>
        </CardSurface>
      </section>

      <section aria-labelledby="profile-avatar-heading" className="flex flex-col gap-3">
        <h2 id="profile-avatar-heading" className="text-base font-semibold text-ink">
          {t('profile.panel.avatar')}
        </h2>

        <CardSurface variant="sheet" className="flex flex-col gap-3 p-4">
          <TextField
            label={t('profile.panel.avatarUrl')}
            type="text"
            hint={t('profile.panel.avatarUrlHint')}
            value={avatarDraft}
            onChange={(event) => {
              setAvatarUrlInvalid(false);
              setAvatarDraft(event.target.value);
            }}
            {...avatarFieldProps}
          />
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              busy={busy?.avatar ?? false}
              disabled={avatarSaveDisabled}
              onClick={handleAvatarSave}
            >
              {t('profile.panel.saveAvatar')}
            </Button>
            {profile.avatarUrl !== null && (
              <Button
                variant="secondary"
                size="sm"
                busy={busy?.avatar ?? false}
                onClick={handleAvatarRemove}
              >
                {t('profile.panel.removeAvatar')}
              </Button>
            )}
          </div>
          {upload !== undefined && (
            <div className="flex flex-col gap-3 border-t border-border pt-3">
              {/*
                `h3`, NOT `h2`. The picker is a sub-region of Avatar rather
                than a fifth peer of the four regions `D1-BRIEF.md` §6.6 fixes,
                and the level is what lets both of this move's oracles stay
                untouched: this file's first test reads
                `getAllByRole('heading', { level: 2 })` and must still see
                exactly four, while `FR-NOTIF-033.spec.ts` asks for a heading
                named "Upload an avatar" with no level at all.

                The id is the one the picker carried while it was a section of
                `routes/profile.tsx`, kept deliberately so that anything
                anchored on it survives the move.
              */}
              <h3
                id="profile-avatar-upload-heading"
                className="text-sm font-semibold text-ink"
              >
                {t('profile.avatarUpload.heading')}
              </h3>
              {/*
                A wrapping `label` rather than a `TextField`: `TextField`
                renders an `input` it controls, and a file input's value cannot
                be controlled. This is the shape `BoardBackgroundPicker` and
                `CardAttachmentSection` already use, so the accessible name
                comes from the span beside it.

                ⚠ Chromium renders its own `Choose File` inside this control
                and that string is NOT routable through the B80 catalogue — a
                known, accepted FE-80.1 residual, not a defect. The label the
                product owns is the span above it.
              */}
              <label className="flex flex-col gap-1 text-sm text-ink">
                <span className="font-medium">{t('profile.avatarUpload.field')}</span>
                <input
                  ref={upload.inputRef}
                  type="file"
                  accept={AVATAR_UPLOAD_CONTENT_TYPES.join(',')}
                  disabled={upload.busy}
                  className="text-sm file:mr-2 file:rounded-card file:border file:border-border-strong file:bg-surface-raised file:px-2 file:py-1 file:text-ink"
                  onChange={(event) => {
                    upload.onFile(event.currentTarget.files?.[0] ?? null);
                  }}
                />
              </label>
              <p className="text-xs text-ink-muted">
                {/*
                  `{size}` is rendered from AVATAR_UPLOAD_MAX_BYTES — the
                  constant the SERVER signs as content-length — so raising the
                  cap cannot leave this sentence claiming the old one. It also
                  says the upload replaces "the avatar link above", which is
                  why this block is the LAST child of the section rather than
                  the first.
                */}
                {t('profile.avatarUpload.hint', {
                  size: formatAttachmentSize(t, AVATAR_UPLOAD_MAX_BYTES),
                })}
              </p>
              {upload.message !== null && (
                /*
                  `role="alert"`, because this message replaces one that was
                  never spoken: the refusal happens on a change the member did
                  not submit, so nothing else would tell a screen-reader user
                  the file was refused. D11's rule is one event, one channel —
                  a FAILED save renders, a successful one announces — and this
                  is the render half.
                */
                <p role="alert" className="text-sm text-bad">
                  {upload.message}
                </p>
              )}
              <div>
                <Button
                  variant="primary"
                  size="sm"
                  busy={upload.busy}
                  /*
                   * ⚠ BOTH DIRECTIONS, NOT ONE. This panel's avatar controls
                   * go busy while the upload runs (`busy.avatar`, which the
                   * route ORs with its upload state), and this button goes
                   * unavailable while the typed-URL save runs — the route
                   * computes that into `submitDisabled`. Closing only the
                   * first would leave the race open the other way: two flights
                   * patching the same column, and the winner is whichever
                   * round trip happened to finish last.
                   */
                  disabled={upload.submitDisabled}
                  onClick={upload.onSubmit}
                >
                  {t('profile.avatarUpload.submit')}
                </Button>
              </div>
            </div>
          )}
        </CardSurface>
      </section>

      <section
        aria-labelledby="profile-initials-preview-heading"
        className="flex flex-col gap-3"
      >
        <h2
          id="profile-initials-preview-heading"
          className="text-base font-semibold text-ink"
        >
          {t('profile.panel.initialsPreview')}
        </h2>

        <CardSurface variant="sheet" className="p-4">
          <figure className="flex items-center gap-3">
            <Avatar profile={{ ...profile, avatarUrl: null }} size="md" />
            <figcaption className="text-sm text-ink-muted">
              {t('profile.panel.initialsPreviewCaption')}
            </figcaption>
          </figure>
        </CardSurface>
      </section>
    </div>
  );
}
